import { Book } from '@/data/books';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

interface BookCardProps {
  book: Book;
  size?: 'default' | 'large';
}

export function BookCard({ book, size = 'default' }: BookCardProps) {
  const isLarge = size === 'large';

  return (
    <Link
      to={`/books/${encodeURIComponent(book.id)}`}
      className="group card-book block"
    >
      {/* Cover Image */}
      <div className={`card-book-cover relative ${isLarge ? 'aspect-[2/3]' : 'aspect-[2/3]'} overflow-hidden`}>
        <div className="absolute inset-0 bg-gradient-to-br from-muted to-secondary flex items-center justify-center">
          <span className="font-serif text-lg text-muted-foreground text-center px-4 leading-tight">
            {book.title}
          </span>
        </div>
        <img
          src={book.coverImage}
          alt={book.title}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />

        {/* Hover Overlay */}
        <div className="absolute inset-0 bg-primary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
          <span className="flex items-center gap-2 text-sm text-primary font-medium">
            <ArrowRight size={16} />
            View Book
          </span>
        </div>
      </div>

      {/* Book Info */}
      <div className={`p-4 ${isLarge ? 'p-6' : 'p-4'}`}>
        <h3 className={`heading-card text-foreground group-hover:text-primary transition-colors duration-300 line-clamp-2 ${isLarge ? 'text-xl' : ''}`}>
          {book.title}
        </h3>
        <p className="text-sm text-primary/90 mt-1 font-medium">by {book.author}</p>

        {(book.readers != null || book.clicks != null || book.engagementRate != null) && (
          <p className="mt-1.5 text-[11px] tracking-wide text-muted-foreground/80">
            {book.readers != null && <span>{book.readers.toLocaleString()} Readers</span>}
            {book.readers != null && book.clicks != null && <span className="mx-1.5 opacity-50">·</span>}
            {book.clicks != null && <span>{book.clicks.toLocaleString()} Clicks</span>}
            {book.engagementRate != null && book.engagementRate > 0 && (
              <>
                <span className="mx-1.5 opacity-50">·</span>
                <span>📈 {Number(book.engagementRate).toFixed(1)}%</span>
              </>
            )}
          </p>
        )}

        {isLarge && (
          <>
            <p className="text-sm text-secondary-foreground mt-3 line-clamp-2">
              {book.description}
            </p>
            <div className="flex items-center justify-between mt-4">
              <span className="text-xs text-muted-foreground uppercase tracking-wider">
                {book.visibility}
              </span>
              <span className="text-xs text-primary font-medium uppercase tracking-wider">
                {book.genre}
              </span>
            </div>
          </>
        )}
      </div>
    </Link>
  );
}
